import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { useT } from '../../../i18n';
import { RowAction } from './AdminUi';

export interface OfficeRoutesSelection {
  office_id: string;
  selectedCollects: string[];
}

interface Props {
  offices: Office[];
  routesByOffice: Record<string, Route[]>;
  value: OfficeRoutesSelection[];
  onChange: (value: OfficeRoutesSelection[]) => void;
  error?: string;
}

/**
 * Sedes y rutas asignadas a un usuario: una tarjeta por sede con la lista de sus rutas para marcar.
 * Controlado (sin estado propio): el formulario guarda la selección.
 */
const OfficeRoutesPicker: React.FC<Props> = ({ offices, routesByOffice, value, onChange, error }) => {
  const t = useT();
  const update = (index: number, patch: Partial<OfficeRoutesSelection>) =>
    onChange(value.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const last = value[value.length - 1];
  const canAdd = offices.length > value.length && (!last || (last.office_id && last.selectedCollects.length > 0));
  const used = value.map((s) => s.office_id).filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      {value.length === 0 && (
        <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
          {error ?? t('users.noOfficesYet')}
        </Typography>
      )}
      {value.map((selection, index) => {
        const routes = routesByOffice[selection.office_id] || [];
        const all = routes.length > 0 && selection.selectedCollects.length === routes.length;
        const some = selection.selectedCollects.length > 0 && !all;
        const options = offices.filter((o) => o.id.toString() === selection.office_id || !used.includes(o.id.toString()));
        const incomplete = !!error && (!selection.office_id || selection.selectedCollects.length === 0);
        return (
          <Paper
            key={index}
            variant="outlined"
            sx={{ p: 2, minWidth: 0, borderColor: incomplete ? 'error.main' : 'divider', bgcolor: 'background.default' }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                id={`office-routes-${index}`}
                select
                size="small"
                label={t('users.office')}
                value={options.length ? selection.office_id : ''}
                onChange={(e) => update(index, { office_id: e.target.value, selectedCollects: [] })}
                sx={{ flex: 1, minWidth: 0, maxWidth: 420 }}
              >
                {options.map((o) => (
                  <MenuItem key={o.id} value={o.id.toString()}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ ml: 'auto' }}>
                <RowAction
                  label={t('users.removeOffice')}
                  icon={<DeleteOutlineIcon fontSize="small" />}
                  color="error"
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                />
              </Box>
            </Box>
            {selection.office_id && (
              <Box sx={{ mt: 1.5 }}>
                {routes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('users.officeWithoutRoutes')}
                  </Typography>
                ) : (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={all}
                          indeterminate={some}
                          onChange={(e) =>
                            update(index, { selectedCollects: e.target.checked ? routes.map((r) => r.id.toString()) : [] })
                          }
                        />
                      }
                      label={<Typography variant="subtitle2">{t('users.allRoutesOfOffice', { count: routes.length })}</Typography>}
                    />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))',
                        columnGap: 2,
                        pl: { xs: 0, sm: 3 },
                      }}
                    >
                      {routes.map((r) => {
                        const id = r.id.toString();
                        const checked = selection.selectedCollects.includes(id);
                        return (
                          <FormControlLabel
                            key={id}
                            sx={{ minWidth: 0, '& .MuiFormControlLabel-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                            control={
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() =>
                                  update(index, {
                                    selectedCollects: checked
                                      ? selection.selectedCollects.filter((c) => c !== id)
                                      : [...selection.selectedCollects, id],
                                  })
                                }
                              />
                            }
                            label={r.name}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Paper>
        );
      })}
      {offices.length > value.length && (
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!canAdd}
            onClick={() => onChange([...value, { office_id: '', selectedCollects: [] }])}
          >
            {t('users.addOffice')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OfficeRoutesPicker;
